import React, { useState, useEffect } from 'react'
import { useI18n } from '../../i18n'
import { Edit, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { TextField } from '@/components/ui/TextField'
import shellStyles from '../tpsm/SupplierFormModal.module.css'
import formStyles from '../../styles/components/assets/AssetFormModal.module.css'
import { assetTypeOptions, normalizeAssetType } from '../../constants/assetTaxonomy'
import { API_ENDPOINTS } from '../../config/api'
interface EditAssetCardProps {
  asset: {
    id: string
    name: string
    type?: string
    environment?: string
    owner?: { id: string; username: string; email?: string } | null
    criticality?: string
    tags?: Array<{ id: string; name: string } | string>
    posture?: {
      os?: string
      patchLevel?: number
      edrInstalled?: boolean
      firewallEnabled?: boolean
      encryptionEnabled?: boolean
    }
    riskScore?: number
    complianceScore?: number
    details?: {
      ip?: string
      hostname?: string
      url?: string
      description?: string
    }
  }
  onAssetUpdated?: () => void
  onClose?: () => void
}

interface User {
  id: string
  username: string
  email?: string
}

const EditAssetCard: React.FC<EditAssetCardProps> = ({ asset, onAssetUpdated, onClose }) => {
  const { t } = useI18n()
  const [modalOpen, setModalOpen] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [formData, setFormData] = useState({
    name: asset.name || '',
    ip: asset.details?.ip || '',
    hostname: asset.details?.hostname || '',
    url: asset.details?.url || '',
    environment: asset.environment || 'PRODUCTION',
    description: asset.details?.description || '',
    type: asset.type || 'OTHER',
    ownerId: asset.owner?.id || '',
    criticality: asset.criticality || '',
    os: asset.posture?.os || '',
    patchLevel: asset.posture?.patchLevel?.toString() || '',
    edrInstalled: asset.posture?.edrInstalled || false,
    firewallEnabled: asset.posture?.firewallEnabled || false,
    encryptionEnabled: asset.posture?.encryptionEnabled || false,
    riskScore: asset.riskScore?.toString() || '',
    complianceScore: asset.complianceScore?.toString() || ''
  })

  useEffect(() => {
    loadUsers()
    const d = asset.details || {}
    setFormData((prev) => ({
      ...prev,
      name: asset.name || prev.name,
      ip: String(d.ip ?? (asset as { ip?: string }).ip ?? prev.ip),
      hostname: String(d.hostname ?? (asset as { hostname?: string }).hostname ?? prev.hostname),
      url: String(d.url ?? (asset as { url?: string }).url ?? prev.url),
      description: String(d.description ?? prev.description),
      type: asset.type || prev.type,
      environment: asset.environment || prev.environment,
      ownerId: asset.owner?.id || prev.ownerId,
      criticality: asset.criticality || prev.criticality,
      riskScore: asset.riskScore?.toString() || prev.riskScore,
      complianceScore: asset.complianceScore?.toString() || prev.complianceScore,
      os: asset.posture?.os || prev.os,
      patchLevel: asset.posture?.patchLevel?.toString() || prev.patchLevel,
      edrInstalled: asset.posture?.edrInstalled ?? prev.edrInstalled,
      firewallEnabled: asset.posture?.firewallEnabled ?? prev.firewallEnabled,
      encryptionEnabled: asset.posture?.encryptionEnabled ?? prev.encryptionEnabled,
    }))
    if (asset.tags && Array.isArray(asset.tags)) {
      const tagNames = asset.tags.map((tag) => (typeof tag === 'string' ? tag : tag.name))
      setTags(tagNames)
    }
  }, [asset])

  const loadUsers = async () => {
    setLoadingUsers(true)
    try {
      const token = localStorage.getItem('token') || ''
      const { getActiveOrgId } = await import('../../utils/orgUtils')
      const orgId = getActiveOrgId()
      
      if (!orgId) {
        setUsers([])
        return
      }
      
      const response = await fetch('/api/organizations/current/users', {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      if (response.ok) {
        const data = await response.json()
        setUsers(Array.isArray(data.users) ? data.users : [])
      }
    } catch (err) {
      console.error('Failed to load users:', err)
      setUsers([])
    } finally {
      setLoadingUsers(false)
    }
  }

  const addTag = () => {
    const tag = tagInput.trim()
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag])
      setTagInput('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken') || ''
      const { getActiveOrgId } = await import('../../utils/orgUtils')
      const orgId = getActiveOrgId()
      if (!orgId) {
        throw new Error(t('assets.edit.noOrganization', 'Organization not found'))
      }

      const pn = (v: string) => {
        const n = parseFloat(String(v))
        return Number.isFinite(n) ? n : undefined
      }

      const payload: Record<string, unknown> = {
        name: formData.name?.trim() || asset.name,
        type: normalizeAssetType(formData.type),
        environment: formData.environment,
        description: formData.description?.trim() || '',
        ip: formData.ip?.trim() || '',
        hostname: formData.hostname?.trim() || '',
        url: formData.url?.trim() || '',
      }
      const ownerUuid = String(formData.ownerId || '').trim()
      if (
        ownerUuid &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(ownerUuid)
      ) {
        payload.ownerId = ownerUuid
      } else {
        payload.ownerId = null
      }
      if (['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].includes(String(formData.criticality || ''))) {
        payload.criticality = formData.criticality
      } else {
        payload.criticality = null
      }
      payload.tags = tags
      const riskNum = pn(formData.riskScore)
      if (riskNum !== undefined) payload.riskScore = riskNum
      const compNum = pn(formData.complianceScore)
      if (compNum !== undefined) payload.complianceScore = compNum

      // Manual security controls — always explicit on edit save
      payload.postureExplicit = true
      payload.os = formData.os?.trim() || ''
      const patchNum = pn(formData.patchLevel)
      if (patchNum !== undefined) payload.patchLevel = patchNum
      else if (formData.patchLevel === '' || formData.patchLevel == null) {
        /* leave unset */
      }
      payload.edrInstalled = !!formData.edrInstalled
      payload.firewallEnabled = !!formData.firewallEnabled
      payload.encryptionEnabled = !!formData.encryptionEnabled

      const response = await fetch(API_ENDPOINTS.ORG_ASSETS_UPDATE(orgId, asset.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(
          data.message || data.error || t('assets.edit.failed', 'Failed to update asset'),
        )
      }

      setModalOpen(false)
      
      if (onAssetUpdated) {
        onAssetUpdated()
      }
      if (onClose) {
        onClose()
      }
    } catch (err: any) {
      setError(err.message || t('assets.edit.error', 'An error occurred'))
      console.error('Failed to update asset:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setModalOpen(false)
    if (onClose) {
      onClose()
    }
  }

  if (!modalOpen) return null

  return (
    <div className={shellStyles.backdrop} role="presentation" onClick={handleClose}>
      <div
        className={`${shellStyles.dialog} ${formStyles.dialogWide}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-asset-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={shellStyles.header}>
          <h2 id="edit-asset-modal-title" className={shellStyles.title}>
            {t('assets.edit.title', 'Edit Asset')}
          </h2>
          <Button
            type="button"
            color="outline"
            iconOnly
            onClick={handleClose}
            disabled={loading}
            className={shellStyles.closeBtn}
            aria-label={t('common.close', 'Close')}
            icon={<X className="h-5 w-5" aria-hidden />}
          >
            <span className="sr-only">{t('common.close', 'Close')}</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className={shellStyles.form}>
          {error && (
            <div className={shellStyles.error} role="alert">
              {error}
            </div>
          )}

          <div className={formStyles.stack}>
            <TextField
              label={t('assets.add.name', 'Name')}
              value={formData.name}
              onChange={(v) => setFormData({ ...formData, name: v })}
              placeholder={t('assets.add.namePlaceholder', 'Asset name')}
              minWidth="100%"
              required
            />

            <div className={formStyles.grid3}>
              <TextField
                label={t('assets.add.ip', 'IP')}
                value={formData.ip}
                onChange={(v) => setFormData({ ...formData, ip: v })}
                placeholder="192.168.1.1"
                minWidth="100%"
              />
              <TextField
                label={t('assets.add.hostname', 'Hostname')}
                value={formData.hostname}
                onChange={(v) => setFormData({ ...formData, hostname: v })}
                placeholder="example.com"
                minWidth="100%"
              />
              <TextField
                label={t('assets.add.url', 'URL')}
                value={formData.url}
                onChange={(v) => setFormData({ ...formData, url: v })}
                placeholder="https://example.com"
                minWidth="100%"
              />
            </div>

            <Select
              label={t('assets.add.type', 'Type')}
              minWidth="100%"
              options={assetTypeOptions(t)}
              value={formData.type}
              onChange={(v) => v != null && setFormData({ ...formData, type: v })}
              placeholder={t('assets.add.type', 'Type')}
            />

            <Select
              label={t('assets.add.environment', 'Environment')}
              minWidth="100%"
              options={[
                { value: 'PRODUCTION', label: t('assets.add.production', 'Production') },
                { value: 'STAGING', label: t('assets.add.staging', 'Staging') },
                { value: 'DEVELOPMENT', label: t('assets.add.development', 'Development') },
                { value: 'TEST', label: t('assets.add.test', 'Test') },
              ]}
              value={formData.environment}
              onChange={(v) => v != null && setFormData({ ...formData, environment: v })}
              placeholder={t('assets.add.environment', 'Environment')}
            />

            <Select
              label={t('assets.add.owner', 'Owner')}
              minWidth="100%"
              options={[
                { value: '', label: t('assets.add.noOwner', 'No Owner') },
                ...users.map((user) => ({
                  value: user.id,
                  label: `${user.username}${user.email ? ` (${user.email})` : ''}`,
                })),
              ]}
              value={formData.ownerId}
              onChange={(v) => setFormData({ ...formData, ownerId: v ?? '' })}
              placeholder={t('assets.add.owner', 'Owner')}
              disabled={loadingUsers}
            />

            <Select
              label={t('assets.add.criticality', 'Criticality')}
              minWidth="100%"
              options={[
                { value: '', label: t('assets.add.noCriticality', 'Not Set') },
                { value: 'CRITICAL', label: t('assets.add.criticalityCritical', 'Critical') },
                { value: 'HIGH', label: t('assets.add.criticalityHigh', 'High') },
                { value: 'MEDIUM', label: t('assets.add.criticalityMedium', 'Medium') },
                { value: 'LOW', label: t('assets.add.criticalityLow', 'Low') },
              ]}
              value={formData.criticality}
              onChange={(v) => setFormData({ ...formData, criticality: v ?? '' })}
              placeholder={t('assets.add.criticality', 'Criticality')}
            />

            <div>
              <p className={formStyles.sectionLabel}>{t('assets.add.tags', 'Tags')}</p>
              <div className={formStyles.tagRow}>
                <div className={formStyles.tagRowField}>
                  <TextField
                    label={false}
                    value={tagInput}
                    onChange={setTagInput}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addTag()
                      }
                    }}
                    placeholder={t('assets.add.tagsPlaceholder', 'Type and press Enter to add tag')}
                    minWidth="100%"
                    aria-label={t('assets.add.tagsPlaceholder', 'Type and press Enter to add tag')}
                  />
                </div>
                <Button
                  type="button"
                  color="primary"
                  size="small"
                  onClick={addTag}
                  icon={<Plus className="h-4 w-4" />}
                  iconOnly
                  aria-label={t('assets.add.addTag', 'Add tag')}
                >
                  <span className="sr-only">{t('assets.add.addTag', 'Add tag')}</span>
                </Button>
              </div>
              {tags.length > 0 && (
                <div className={formStyles.tagChips}>
                  {tags.map((tag) => (
                    <span key={tag} className={formStyles.tagChip}>
                      {tag}
                      <Button
                        type="button"
                        color="outline"
                        size="small"
                        onClick={() => removeTag(tag)}
                        className="!border-transparent !bg-transparent !p-0 !text-blue-600 !shadow-none hover:!text-blue-800"
                        iconOnly
                        icon={<X className="h-3 w-3" />}
                        aria-label={t('common.remove', 'Remove')}
                      >
                        <span className="sr-only">{t('common.remove', 'Remove')}</span>
                      </Button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className={shellStyles.grid2}>
              <TextField
                label={t('assets.add.os', 'Operating System')}
                value={formData.os}
                onChange={(v) => setFormData({ ...formData, os: v })}
                placeholder="Linux, Windows, etc."
                minWidth="100%"
              />
              <TextField
                label={t('assets.add.patchLevel', 'Patch Level (%)')}
                type="number"
                min={0}
                max={100}
                value={formData.patchLevel}
                onChange={(v) => setFormData({ ...formData, patchLevel: v })}
                placeholder="0-100"
                minWidth="100%"
              />
            </div>

            <div className={shellStyles.grid2}>
              <TextField
                label={t('assets.add.riskScore', 'Risk Score')}
                type="number"
                min={0}
                max={100}
                value={formData.riskScore}
                onChange={(v) => setFormData({ ...formData, riskScore: v })}
                placeholder="0-100"
                minWidth="100%"
              />
              <TextField
                label={t('assets.add.complianceScore', 'Compliance Score')}
                type="number"
                min={0}
                max={100}
                value={formData.complianceScore}
                onChange={(v) => setFormData({ ...formData, complianceScore: v })}
                placeholder="0-100"
                minWidth="100%"
              />
            </div>

            <div>
              <p className={formStyles.sectionLabel}>{t('assets.add.securityPosture', 'Security Posture')}</p>
              <div className={formStyles.checkboxGroup}>
                <label className={shellStyles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.edrInstalled}
                    onChange={(e) => setFormData({ ...formData, edrInstalled: e.target.checked })}
                    className={shellStyles.checkbox}
                  />
                  <span className={shellStyles.checkboxText}>{t('assets.add.edrInstalled', 'EDR Installed')}</span>
                </label>
                <label className={shellStyles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.firewallEnabled}
                    onChange={(e) => setFormData({ ...formData, firewallEnabled: e.target.checked })}
                    className={shellStyles.checkbox}
                  />
                  <span className={shellStyles.checkboxText}>{t('assets.add.firewallEnabled', 'Firewall Enabled')}</span>
                </label>
                <label className={shellStyles.checkboxRow}>
                  <input
                    type="checkbox"
                    checked={formData.encryptionEnabled}
                    onChange={(e) => setFormData({ ...formData, encryptionEnabled: e.target.checked })}
                    className={shellStyles.checkbox}
                  />
                  <span className={shellStyles.checkboxText}>{t('assets.add.encryptionEnabled', 'Encryption Enabled')}</span>
                </label>
              </div>
            </div>

            <TextField
              label={t('assets.add.description', 'Description')}
              multiline
              rows={3}
              value={formData.description}
              onChange={(v) => setFormData({ ...formData, description: v })}
              placeholder={t('assets.add.descriptionPlaceholder', 'Optional description')}
              minWidth="100%"
            />
          </div>

          <div className={shellStyles.footer}>
            <Button type="button" color="outline" onClick={handleClose} disabled={loading}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={loading}
              icon={loading ? undefined : <Edit className="h-4 w-4" aria-hidden />}
              iconPosition="end"
            >
              {loading ? t('assets.edit.updating', 'Updating...') : t('assets.edit.updateAsset', 'Update Asset')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditAssetCard


